import { useCallback, useEffect, useMemo, useState } from 'react';
import { DollarSign, Hash, Cpu, Layers, Calendar, Sparkles } from 'lucide-react';
import api from '../../lib/axios';

const GROUPS = [
  ['feature', 'By feature', Layers],
  ['model', 'By model', Cpu],
  ['day', 'By day', Calendar],
];
const RANGES = [
  ['7', 'Last 7 days'],
  ['30', 'Last 30 days'],
  ['', 'All time'],
];

const fmtUsd = (v) => `$${Number(v || 0).toFixed(4)}`;
const fmtInt = (v) => Number(v || 0).toLocaleString();

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - Number(days));
  return d.toISOString().slice(0, 10);
}

export default function AIUsage() {
  const [groupBy, setGroupBy] = useState('feature');
  const [range, setRange] = useState('30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { group_by: groupBy };
      if (range) params.from = isoDaysAgo(range);
      const r = await api.get('api/v1/ai-usage', { params });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [groupBy, range]);

  useEffect(() => { load(); }, [load]);

  const maxCost = useMemo(() => {
    if (!data?.rows?.length) return 0;
    return Math.max(...data.rows.map((r) => Number(r.cost_usd)));
  }, [data]);

  return (
    <div className="aiusage-page">
      <div className="aiusage-header">
        <div className="aiusage-title-icon"><Sparkles size={20} /></div>
        <div>
          <h1 className="aiusage-title">AI Usage &amp; Cost</h1>
          <p className="aiusage-sub">
            Estimated token spend per feature. Ground truth for the billed total
            is your OpenAI dashboard — these are in-app estimates.
          </p>
        </div>
      </div>

      <div className="aiusage-controls">
        <div className="aiusage-toggle">
          {GROUPS.map(([key, label, Icon]) => (
            <button
              key={key}
              className={`aiusage-toggle-btn ${groupBy === key ? 'active' : ''}`}
              onClick={() => setGroupBy(key)}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
        <select className="aiusage-range" value={range}
          onChange={(e) => setRange(e.target.value)}>
          {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {error && <div className="aiusage-empty">{error}</div>}
      {loading && !data && <div className="aiusage-loading">Loading…</div>}

      {data && !error && (
        <>
          <div className="stats-grid">
            <StatTile icon={<DollarSign size={18} />} label="Total cost (est.)"
              value={fmtUsd(data.total_cost_usd)} />
            <StatTile icon={<Hash size={18} />} label="Total calls"
              value={fmtInt(data.total_calls)} />
            <StatTile icon={<Cpu size={18} />} label="Prompt tokens"
              value={fmtInt(data.total_prompt_tokens)} />
            <StatTile icon={<Cpu size={18} />} label="Completion tokens"
              value={fmtInt(data.total_completion_tokens)} />
          </div>

          {data.rows.length === 0 ? (
            <div className="aiusage-empty">
              No AI usage recorded for this range yet.
            </div>
          ) : (
            <table className="agent-table aiusage-table">
              <thead>
                <tr>
                  <th>{groupBy === 'day' ? 'Day' : groupBy === 'model' ? 'Model' : 'Feature'}</th>
                  <th>Calls</th>
                  <th>Prompt</th>
                  <th>Completion</th>
                  <th>Cost (est.)</th>
                  <th className="aiusage-share-col">Share</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.key}>
                    <td className="aiusage-key">{r.key.replace(/_/g, ' ')}</td>
                    <td>{fmtInt(r.calls)}</td>
                    <td>{fmtInt(r.prompt_tokens)}</td>
                    <td>{fmtInt(r.completion_tokens)}</td>
                    <td className="aiusage-cost">{fmtUsd(r.cost_usd)}</td>
                    <td className="aiusage-share-col">
                      <div className="aiusage-bar-track">
                        <div className="aiusage-bar-fill" style={{
                          width: maxCost > 0
                            ? `${Math.max(2, (Number(r.cost_usd) / maxCost) * 100)}%`
                            : '0%',
                        }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

function StatTile({ icon, label, value }) {
  return (
    <div className="stat-card">
      <div className="stat-icon">{icon}</div>
      <div className="stat-content">
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
      </div>
    </div>
  );
}
